'use client';

import { forwardRef, useEffect, useRef, useState } from 'react';
import { cn } from '@shared/utils';
import { SeparatorIcon } from '@shared/ui/src/components/decorative';

/**
 * @group Public
 * @summary Reusable section wrapper with consistent padding, optional inverted background, and oversized heading support
 * 
 * Provides consistent section structure for public pages with:
 * - Consistent vertical padding
 * - Optional inverted (deep teal) background for chapter rhythm
 * - Optional oversized heading for chapter transitions
 * - Responsive horizontal padding
 * 
 * @example
 * ```tsx
 * <PublicSection inverted heading="Our Promise">
 *   <p>Content here</p>
 * </PublicSection>
 * ```
 */
interface PublicSectionProps {
  /** Section content */
  children?: React.ReactNode;
  /** Whether to use the inverted (deep teal) background */
  inverted?: boolean;
  /** Whether the section should be indented (more padding) vs poking out (less padding) */
  indented?: boolean;
  /** Optional oversized heading text displayed above children */
  heading?: string;
  /** Optional subheading text */
  subheading?: string;
  /** Optional heading font color (CSS color value or Tailwind class) */
  headingColor?: string;
  /** Optional subheading font color (CSS color value or Tailwind class) */
  subheadingColor?: string;
  /** Optional separator icon color (CSS color value or Tailwind class) */
  separatorColor?: string;
  /** Optional separator line color (CSS color value or Tailwind class) */
  separatorLineColor?: string;
  /** Additional CSS classes for the section element */
  className?: string;
  /** HTML id for anchor linking */
  id?: string;
  /** Optional inline styles for the section element */
  style?: React.CSSProperties;
}

export const PublicSection = forwardRef<HTMLElement, PublicSectionProps>(({
  children,
  inverted = false,
  indented = false,
  heading,
  subheading,
  headingColor,
  subheadingColor,
  separatorColor,
  separatorLineColor,
  className,
  id,
  style,
}, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const internalRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = internalRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);

  // Combine refs: handle both forwarded ref and internal ref
  const setRefs = (node: HTMLElement | null) => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  return (
    <section
      ref={setRefs}
      id={id}
      className={cn(
        'py-20 lg:py-24 transition-opacity duration-700 ease-out',
        indented 
          ? 'px-8 sm:px-12 lg:px-16 xl:px-24' 
          : 'px-4 sm:px-6 lg:px-8',
        isVisible ? 'opacity-100' : 'opacity-0',
        inverted
          ? 'bg-section-inverted text-section-inverted-foreground'
          : 'bg-background text-foreground',
        className
      )}
      style={style}
    >
      <div className="max-w-7xl mx-auto">
        {heading && (
          <div className="mb-12 text-center">
            <h2 
              className={cn(
                'text-3xl sm:text-4xl lg:text-5xl font-bold font-serif mb-2'
              )}
              style={{ 
                ...(headingColor && { color: headingColor })
              }}
            >
              {heading}
            </h2>
            <div className="flex items-center justify-center ">
              <div 
                className="flex-1 h-px bg-gradient-to-r from-transparent via-muted-foreground/40 to-transparent" 
                style={
                  separatorLineColor
                    ? { background: `linear-gradient(to right, transparent, ${separatorLineColor}, transparent)` }
                    : inverted
                    ? { background: 'linear-gradient(to right, transparent, hsl(var(--section-inverted-foreground) / 0.4), transparent)' }
                    : undefined
                }
              />
              <div 
                className="px-5"
                style={{ backgroundColor: 'transparent' }}
              >
                <SeparatorIcon 
                  size={100} 
                  className={cn(
                    separatorColor ? '' : inverted ? 'text-section-inverted-foreground/80' : 'text-muted-foreground/60'
                  )}
                  color={separatorColor || (inverted ? 'hsl(var(--section-inverted-foreground) / 0.8)' : undefined)}
                />
              </div>
              <div 
                className="flex-1 h-px bg-gradient-to-r from-transparent via-muted-foreground/40 to-transparent" 
                style={
                  separatorLineColor
                    ? { background: `linear-gradient(to right, transparent, ${separatorLineColor}, transparent)` }
                    : inverted
                    ? { background: 'linear-gradient(to right, transparent, hsl(var(--section-inverted-foreground) / 0.4), transparent)' }
                    : undefined
                }
              />
            </div>
            {subheading && (
              <p 
                className={cn(
                  'text-lg sm:text-xl max-w-3xl mx-auto',
                  !subheadingColor && (inverted ? 'text-section-inverted-foreground/90' : 'text-muted-foreground')
                )}
                style={subheadingColor ? { color: subheadingColor } : undefined}
              >
                {subheading}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
});

PublicSection.displayName = 'PublicSection';
